import { useEffect, useState } from 'react'
import { User } from '../../types'
import {
  loadDashboard,
  DashboardSnapshot,
  ActionItem,
} from '../../services/dashboardService'
import Icon from './icons'
import './TodayView.css'

interface TodayViewProps {
  user?: User
  onNavigate?: (view: string) => void
}

const ic = (tablerName: string) => tablerName.replace(/^ti-/, '')

const WHY = [
  ['radar-2', 'Sourcing', 'Listings valued & ranked before you look'],
  ['shield-check', 'Due diligence', 'Documents read & matched automatically'],
  ['cash', 'Payments', 'Two-person approval enforced, not remembered'],
  ['mail-fast', 'Collections', 'Overdue invoices chase themselves'],
  ['receipt-tax', 'GST', 'Margin scheme credits captured without thinking'],
]

export default function TodayView({ user, onNavigate }: TodayViewProps) {
  const [snap, setSnap] = useState<DashboardSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const firstName = user?.full_name?.split(' ')[0]

  useEffect(() => {
    let alive = true
    setLoading(true)
    loadDashboard(firstName)
      .then((s) => {
        if (alive) {
          setSnap(s)
          setError(null)
        }
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : 'Could not load your dashboard')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [firstName])

  const go = (view: string) => {
    if (onNavigate) onNavigate(view)
  }

  if (loading) {
    return (
      <div className="today-v2">
        <div className="t2-phead">
          <div className="t2-eyebrow"><Icon name="bolt" size={14} /> Loading your day…</div>
          <h2>Getting everything ready</h2>
        </div>
        <div className="t2-hero">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="t2-hstat t2-skeleton" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !snap) {
    return (
      <div className="today-v2">
        <div className="t2-phead">
          <div className="t2-eyebrow"><Icon name="bolt" size={14} /> Today</div>
          <h2>Dashboard unavailable</h2>
          <p>{error || 'No data to show yet.'}</p>
        </div>
      </div>
    )
  }

  const ActCard = ({ a }: { a: ActionItem }) => (
    <div
      className={`t2-act ${a.severity === 'urgent' ? 'urgent' : a.severity === 'go' ? 'go' : ''} ${a.severity === 'done' ? 't2-act-done' : ''} ${onNavigate ? 'clickable' : ''}`}
      onClick={() => go(a.view)}
    >
      <div className="t2-act-ic" style={{ background: a.iconBg, color: a.iconColor }}>
        <Icon name={ic(a.icon)} size={24} />
      </div>
      <div className="t2-act-main">
        <div className="t2-act-k">{a.kicker}</div>
        <div className="t2-act-t">{a.title}</div>
        <div className="t2-act-s">{a.detail}</div>
      </div>
      <div className="t2-act-cta">
        {a.amount && (
          <span className="t2-amt" style={{ color: a.amountColor || 'var(--txt)' }}>{a.amount}</span>
        )}
        <div className="t2-act-arrow">
          <Icon name={a.severity === 'done' ? 'check' : 'arrow-right'} size={18} />
        </div>
      </div>
    </div>
  )

  const maxFlow = Math.max(1, ...snap.flow.map((f) => f.count))

  return (
    <div className="today-v2">
      <div className="t2-phead">
        <div className="t2-eyebrow"><Icon name="bolt" size={14} /> {snap.eyebrow}</div>
        <div className="t2-phead-row">
          <div>
            <h2>{snap.headline}</h2>
            <p>{snap.sub}</p>
          </div>
          <button className="t2-btn primary" onClick={() => go('sourced')}>
            <Icon name="plus" size={17} /> Source a deal
          </button>
        </div>
      </div>

      <div className="t2-hero">
        {snap.hero.map((h) => (
          <div
            key={h.label}
            className={`t2-hstat ${onNavigate ? 'clickable' : ''}`}
            onClick={() => go(h.view)}
          >
            <div className="t2-hl"><Icon name={ic(h.icon)} size={15} /> {h.label}</div>
            <div className="t2-hn">{h.value}</div>
            <div className={`t2-hs ${h.trend}`}>
              {h.trend === 'up' && <Icon name="arrow-up-right" size={13} />}
              {h.trend === 'down' && <Icon name="arrow-down-right" size={13} />}
              {h.detail}
            </div>
          </div>
        ))}
      </div>

      {snap.insights.length > 0 && (
        <div className="t2-brain">
          <div className="t2-brain-h">
            <div className="t2-brain-orb"><Icon name="sparkles" size={19} /></div>
            <div className="t2-bt">
              Direct Auto Brain
              <small>What the numbers are telling you today</small>
            </div>
            <div className="t2-live">Live</div>
          </div>
          <div className="t2-insights">
            {snap.insights.map((ins, i) => {
              const mx = Math.max(...ins.spark) || 1
              return (
                <div
                  key={i}
                  className={`t2-insight ${onNavigate ? 'clickable' : ''}`}
                  onClick={() => go(ins.view)}
                >
                  <div className="t2-ik" style={{ color: ins.color }}>
                    <Icon name={ic(ins.icon)} size={15} /> {ins.label}
                  </div>
                  <div className="t2-iv">{ins.value}</div>
                  <div className="t2-is">{ins.body}</div>
                  <div className="t2-spark">
                    {ins.spark.map((v, j) => (
                      <i key={j} style={{ height: `${Math.max(15, Math.round((v / mx) * 100))}%` }} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          {snap.recommendation && (
            <div
              className={`t2-brain-foot ${onNavigate ? 'clickable' : ''}`}
              onClick={() => go(snap.recommendation!.view)}
            >
              <Icon name="bulb" size={16} />
              <div className="t2-reco">{snap.recommendation.text}</div>
              <Icon name="arrow-right" size={16} />
            </div>
          )}
        </div>
      )}

      <div className="t2-stream">
        <div className="t2-stream-div play"><Icon name="player-play-filled" size={13} /> Needs you now</div>
        {snap.needsYou.length === 0 ? (
          <div className="t2-allclear">
            <Icon name="circle-check" size={42} />
            <div className="t2-ac-t">You're all caught up</div>
            <div className="t2-ac-s">Nothing needs your approval right now.</div>
          </div>
        ) : (
          snap.needsYou.map((a) => <ActCard key={a.key} a={a} />)
        )}

        {snap.comingUp.length > 0 && (
          <>
            <div className="t2-stream-div">Coming up</div>
            {snap.comingUp.map((a) => <ActCard key={a.key} a={a} />)}
          </>
        )}

        {snap.handled.length > 0 && (
          <>
            <div className="t2-stream-div">Handled automatically</div>
            {snap.handled.map((a) => <ActCard key={a.key} a={a} />)}
          </>
        )}
      </div>

      <div className="t2-grid2">
        <div className="t2-card">
          <div className="t2-card-h">
            <h3>Deal flow</h3>
            <span className="t2-chip ok">live pipeline</span>
          </div>
          <div className="t2-flow">
            {snap.flow.map((stage) => (
              <div key={stage.label} className="t2-flow-stage">
                <div className="t2-flow-wrap">
                  <div
                    className="t2-flow-bar"
                    style={{ height: `${Math.max(4, (stage.count / maxFlow) * 100)}%` }}
                  >
                    <span className="t2-flow-count">{stage.count}</span>
                  </div>
                </div>
                <div className="t2-flow-label">{stage.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="t2-card">
          <div className="t2-card-h">
            <h3>Why this beats a clipboard</h3>
          </div>
          <div className="t2-why">
            {WHY.map((x) => (
              <div key={x[1]} className="t2-why-row">
                <div className="t2-why-ic"><Icon name={x[0]} size={17} /></div>
                <div>
                  <div className="t2-why-t">{x[1]}</div>
                  <div className="t2-why-s">{x[2]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
